import { useEffect, useState } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import MagneticButton from "./MagneticButton";
import { company } from "../data/site";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Completed Projects", to: "/projects/completed" },
  { label: "Ongoing Projects", to: "/projects/ongoing" },
  { label: "Services", to: "/services" },
  { label: "Clientele", to: "/clients" },
  { label: "Contact Us", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 110, damping: 24 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "py-2" : "py-4"
        }`}
      >
        <div
          className={`mx-3 md:mx-6 rounded-2xl border ${
            scrolled
              ? "glass shadow-[0_10px_40px_-15px_rgba(0,0,0,0.6)] border-white/10"
              : "bg-transparent border-transparent"
          } transition-all duration-500`}
        >
          <div className="px-4 md:px-6 py-2.5">
            {/* Mobile View: Logo on left, Hamburger on right */}
            <div className="flex lg:hidden items-center justify-between w-full">
              <Logo testId="nav-logo-mobile" logoClass="h-12" />
              <button
                data-testid="mobile-menu-toggle"
                className="h-11 w-11 grid place-items-center rounded-full border border-white/10 hover:border-[#C96A2B] hover:text-[#C96A2B] transition-colors"
                onClick={() => setOpen((v) => !v)}
                aria-label="Toggle menu"
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>

            {/* Desktop View: Stacked Layout */}
            <div className="hidden lg:flex flex-col gap-3 w-full">
              {/* Row 1: Left Phone, Center Logo, Right CTA */}
              <div className="flex items-center justify-between w-full relative">
                {/* Left: Phone */}
                <div className="w-1/3 flex justify-start">
                  <a
                    href={`tel:${company.phone.replace(/\s/g, "")}`}
                    data-testid="nav-phone"
                    className="flex items-center gap-2 text-xs text-white/60 hover:text-[#C96A2B] transition-colors"
                  >
                    <Phone size={14} />
                    <span className="tracking-wide font-medium">{company.phone}</span>
                  </a>
                </div>

                {/* Center: Large Logo */}
                <div className="w-1/3 flex justify-center">
                  <Logo
                    testId="nav-logo-desktop"
                    logoClass={scrolled ? "h-12 lg:h-[60px]" : "h-16 lg:h-[90px]"}
                  />
                </div>

                {/* Right: CTA Button */}
                <div className="w-1/3 flex justify-end">
                  <MagneticButton
                    href="/consultation"
                    testId="nav-cta-consult"
                    className="!py-2.5 !px-5 !text-xs"
                  >
                    Book Consultation
                  </MagneticButton>
                </div>
              </div>

              {/* Row 2: Menu Pages Centered Below Logo */}
              <div className="flex justify-center border-t border-white/10 pt-2.5 mt-0.5">
                <nav className="flex items-center gap-1.5">
                  {NAV.map((n) => (
                    <NavLink
                      key={n.label}
                      to={n.to}
                      end={n.to === "/"}
                      data-testid={`nav-link-${n.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `relative px-4 py-1.5 text-[13px] tracking-wide font-medium transition-colors ${
                          isActive ? "text-white" : "text-white/65 hover:text-white"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <span className="relative inline-flex items-center">
                          {n.label}
                          {isActive && (
                            <motion.span
                              layoutId="nav-active"
                              className="absolute -bottom-1.5 left-2 right-2 h-px bg-[#C96A2B]"
                              transition={{ type: "spring", stiffness: 380, damping: 30 }}
                            />
                          )}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          <motion.div
            data-testid="scroll-progress"
            className="origin-left h-[2px] bg-[#C96A2B] rounded-b-2xl"
            style={{ scaleX }}
          />
        </div>
      </motion.header>

      {/* Mobile drawer */}
      <motion.div
        data-testid="mobile-menu-panel"
        initial={false}
        animate={{ x: open ? 0 : "100%" }}
        transition={{ type: "tween", ease: [0.7, 0, 0.2, 1], duration: 0.5 }}
        className="fixed top-0 right-0 bottom-0 w-[88%] max-w-sm z-40 bg-[#0a0a0c] border-l border-white/10 pt-24 px-8 lg:hidden overflow-y-auto"
      >
        <ul className="flex flex-col gap-4">
          {NAV.map((n) => (
            <li key={n.label}>
              <Link
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className="font-display text-3xl tracking-tight block py-2 hover:text-[#C96A2B] transition-colors"
              >
                {n.label}
              </Link>
            </li>
          ))}
        </ul>
        <div className="mt-8 space-y-4 pb-12">
          <a
            href={`tel:${company.phone.replace(/\s/g, "")}`}
            className="flex items-center gap-3 text-white/70 text-sm"
          >
            <Phone size={16} className="text-[#C96A2B]" />
            {company.phone}
          </a>
          <MagneticButton
            href="/consultation"
            testId="mobile-cta-consult"
            onClick={() => setOpen(false)}
          >
            Book Consultation
          </MagneticButton>
        </div>
      </motion.div>
    </>
  );
}
